import type { NavigationFailure } from 'vue-router';
import type {
  DoStepParams,
  FlowDirection,
  FlowOrder,
  FlowProcess,
  FlowProcessSkipData,
  FlowRouter,
  FlowStep,
} from './FlowRouter.types';

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useCarStore } from '@/entities/Car';
import { useQuoteStore, useTariffsStore } from '@/entities/Quote';
import { TariffType } from '@/entities/Quote/model/Quote.types';
import { useUserStore } from '@/entities/User';
import { useABTestStore } from '@/features/flow/ABTesting';

export function buildFlowRouter(pathHead: string, flowOrder: FlowProcess[], homeUrl = '/'): FlowRouter {
  const appRouter = useRouter();
  const route = useRoute();

  const getPathByName = (routeName: string) => appRouter.resolve({ name: routeName }).path;

  const flowOrderMap = computed<FlowOrder>(
    () =>
      new Map(
        flowOrder.map((step, idx, steps) => [
          step.name,
          {
            path: getPathByName(step.name),
            prev: steps[idx - 1] ?? null,
            next: steps[idx + 1] ?? null,
            current: step,
            index: idx,
          },
        ]),
      ),
  );

  const withParams = (path: string, params?: DoStepParams['query']) => ({
    path,
    query: {
      ...route.query,
      ...params,
    },
  });

  const getFlowRouteByPath = (path: string) => {
    const maybeFlowRoutePath = path.split(`${pathHead}/`).pop();
    return flowOrderMap.value.get(maybeFlowRoutePath!);
  };

  const firstStepName = flowOrder[0]?.name;
  const firstStep = firstStepName ? flowOrderMap.value.get(firstStepName)! : null;
  if (!firstStep) throw new Error('[FlowRouter] flowOrder is empty or first step not resolved');

  const currentStep = ref<FlowStep>(getFlowRouteByPath(route.path) ?? firstStep);
  const previousStep = ref<FlowStep | null>(null);
  const flowDirection = ref<FlowDirection>('forward');

  const isNavigating = ref(false);
  let navigationPromise: Promise<void | NavigationFailure | undefined> = Promise.resolve(undefined);

  // Sync currentStep with route changes caused by browser back/forward navigation
  watch(
    () => route.path,
    (newPath) => {
      const newStep = getFlowRouteByPath(newPath);
      if (newStep && newStep.path !== currentStep.value.path) {
        currentStep.value = newStep;
      }
    },
  );

  const checkIsSkip = (step: FlowProcess, direction: FlowDirection): boolean => {
    // TODO: replace with flowStore
    const carStore = useCarStore();
    const userStore = useUserStore();
    const abTestStore = useABTestStore();
    const quoteStore = useQuoteStore();
    const tariffsStore = useTariffsStore();

    const appState: FlowProcessSkipData = {
      flowDirection: direction,
      phone: userStore.state.phone,
      vin: carStore.state.vin,
      isCarDetailsFulfilled: carStore.isCarDetailsFulfilled,
      isPrefillExperiment: abTestStore.isPrefillExperiment,
      shouldSkipDeductible: quoteStore.state.tariffType === TariffType.TPL,
      hasOnlyOneComprehensiveTariff: tariffsStore.comprehensive?.tariffs.length === 1,
    };

    if (typeof step.isSkip === 'function') return step.isSkip(appState);
    if (direction === 'forward' && typeof step.isForwardSkip === 'function') return step.isForwardSkip(appState);
    if (direction === 'backward' && typeof step.isBackwardSkip === 'function') return step.isBackwardSkip(appState);

    return false;
  };

  const appRouterGoTo = (
    path: string,
    query: DoStepParams['query'],
    method: 'push' | 'replace' = 'push',
  ) => appRouter[method](withParams(path, query));

  let customBackwardAction: ((...args: any[]) => any) | null = null;

  const resolveNextCandidate = (direction: FlowDirection, pageName?: string): FlowStep | undefined => {
    const directionKey = direction === 'forward' ? 'next' : 'prev';
    const proposal = currentStep.value[directionKey];
    return flowOrderMap.value.get(pageName || proposal?.name || '');
  };

  const resolveWithSkips = (candidate: FlowStep | undefined, direction: FlowDirection): FlowStep | undefined => {
    const directionKey = direction === 'forward' ? 'next' : 'prev';

    let step = candidate;
    const visited = new Set<string>();

    while (step && checkIsSkip(step.current, direction)) {
      if (visited.has(step.current.name)) {
        return step;
      }
      visited.add(step.current.name);

      const next = step[directionKey];
      step = next ? flowOrderMap.value.get(next.name) : undefined;
    }

    return step;
  };

  const resolveBackwardPossibleTarget = (): FlowStep => {
    const possibleBackwardTo = currentStep.value.current.possibleBackwardTo!;
    const prevRouteName = previousStep.value?.current.name;

    const [fallbackName] = Array.from(possibleBackwardTo);
    const fallbackTo = flowOrderMap.value.get(fallbackName)!;

    if (!prevRouteName) return fallbackTo;

    const allowed = possibleBackwardTo.has(prevRouteName);
    return allowed && previousStep.value ? previousStep.value : fallbackTo;
  };

  const doStep = (params: DoStepParams): Promise<void | NavigationFailure | undefined> => {
    if (isNavigating.value && !params.force) return navigationPromise;

    isNavigating.value = true;

    const run = async (): Promise<void | NavigationFailure | undefined> => {
      const { direction, pageName, redirectURL, replace = false, query } = params;

      if (direction === 'backward' && customBackwardAction !== null) {
        const action = customBackwardAction;
        customBackwardAction = null;

        return Promise.resolve(action());
      }

      if (direction === 'backward' && currentStep.value.current.possibleBackwardTo) {
        const target = resolveBackwardPossibleTarget();

        const targetPrev = target.prev ? flowOrderMap.value.get(target.prev.name) ?? null : null;

        flowDirection.value = direction;
        if (!replace) previousStep.value = targetPrev;

        const res = await appRouterGoTo(target.path, query, 'push');
        currentStep.value = target;
        return res;
      }

      const candidate = resolveNextCandidate(direction, pageName);

      if (!candidate) {
        if (redirectURL) window.location.href = redirectURL;
        return;
      }

      const finalStep = resolveWithSkips(candidate, direction);

      if (!finalStep) {
        if (redirectURL) window.location.href = redirectURL;
        return;
      }

      flowDirection.value = direction;

      if (!replace) previousStep.value = currentStep.value;

      const methodToUse: 'push' | 'replace' = replace ? 'replace' : 'push';

      const res = await appRouterGoTo(finalStep.path, query, methodToUse);
      currentStep.value = finalStep;

      return res;
    };

    navigationPromise = run().finally(() => {
      isNavigating.value = false;
      navigationPromise = Promise.resolve(undefined);
    });

    return navigationPromise;
  };

  const nextStep = (params?: Partial<DoStepParams>) =>
    doStep({
      ...params,
      direction: 'forward',
    });

  const prevStep = (params?: Partial<DoStepParams>, _awaitLoading?: boolean) =>
    doStep({
      ...params,
      direction: 'backward',
      redirectURL: homeUrl,
    });

  const goToFirstStep = (params?: Partial<DoStepParams>) => {
    if (isNavigating.value) return navigationPromise;

    isNavigating.value = true;

    const run = async (): Promise<void | NavigationFailure | undefined> => {
      flowDirection.value = 'forward';

      const target = firstStep;

      if (checkIsSkip(target.current, 'forward')) {
        currentStep.value = target;
        return nextStep(params);
      }

      const to = withParams(target.path);
      if (params?.query) {
        to.query = {
          ...to.query,
          ...params.query,
        };
      }

      const res = await appRouter.replace(to);

      currentStep.value = target;
      previousStep.value = null;

      return res;
    };

    navigationPromise = run().finally(() => {
      isNavigating.value = false;
      navigationPromise = Promise.resolve(undefined);
    });

    return navigationPromise;
  };

  const getFirstStep = () => firstStep;
  const getFirstStepPath = () => firstStep.path;

  const routeMeta = computed(() => route.meta);

  return {
    currentStep,
    previousStep,
    flowDirection,
    routeMeta,
    isNavigating,

    nextStep,
    prevStep,
    goToFirstStep,
    getFirstStepPath,
    getFirstStep,

    get pathHead() {
      return pathHead;
    },
    get steps() {
      return flowOrderMap.value.size;
    },

    get customBackwardAction() {
      return this.customBackwardAction;
    },

    set customBackwardAction(action: ((...args: any[]) => any) | null) {
      customBackwardAction = action;
    },
  };
}
